import { Injectable } from '@angular/core';
import { ErrorHandler } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ChunkLoadingErrorHandlerService implements ErrorHandler {
  private readonly RELOAD_KEY = 'chunk_reload_attempts';
  private readonly MAX_RELOAD_ATTEMPTS = 2;
  private readonly RELOAD_COOLDOWN = 5000; // 5 seconds
  private readonly POPUP_SHOWN_KEY = 'chunk_error_popup_shown';

  handleError(error: any): void {
    // Check if this is a chunk loading error
    if (this.isChunkLoadingError(error)) {
      console.warn('Chunk loading error detected. Showing update dialog...', error);
      this.handleChunkLoadingError();
    } else {
      // For other errors, log them normally
      console.error('Application error:', error);
    }
  }

  /**
   * Checks if the error is a chunk loading error
   */
  private isChunkLoadingError(error: any): boolean {
    if (!error) {
      return false;
    }

    const errorMessage = error.message || error.toString() || '';
    const errorName = error.name || '';

    // Check for common chunk loading error patterns
    const chunkErrorPatterns = [
      'Failed to fetch dynamically imported module',
      'Loading chunk',
      'Loading CSS chunk',
      'ChunkLoadError',
      'Failed to load chunk',
      'Importing a module script failed'
    ];

    return chunkErrorPatterns.some(pattern => 
      errorMessage.includes(pattern) || errorName.includes(pattern)
    );
  }

  /**
   * Handles chunk loading errors by showing popup instead of auto-reloading
   */
  private handleChunkLoadingError(): void {
    try {
      // Check if we've already shown the popup for this error
      const popupShown = sessionStorage.getItem(this.POPUP_SHOWN_KEY);
      if (popupShown === 'true') {
        // Popup already shown, use fallback reload logic
        this.handleFallbackReload();
        return;
      }

      // Mark popup as shown
      sessionStorage.setItem(this.POPUP_SHOWN_KEY, 'true');

      // Dispatch event to show popup
      window.dispatchEvent(new CustomEvent('app-version-update'));
    } catch (reloadError) {
      console.error('Error during chunk loading error handling:', reloadError);
    }
  }

  /**
   * Fallback reload logic if popup was already shown
   */
  private handleFallbackReload(): void {
      const reloadData = this.getReloadData();
      const now = Date.now();

      // Check if we're in a cooldown period
      if (reloadData.lastReloadTime && (now - reloadData.lastReloadTime) < this.RELOAD_COOLDOWN) {
        console.warn('Chunk reload cooldown active. Skipping reload.');
        return;
      }

      // Check if we've exceeded max reload attempts
      if (reloadData.attempts >= this.MAX_RELOAD_ATTEMPTS) {
        console.error('Max chunk reload attempts reached. Please refresh the page manually.');
        this.clearReloadData();
        return;
      }

      // Increment reload attempts
      const newAttempts = reloadData.attempts + 1;
      this.setReloadData({
        attempts: newAttempts,
        lastReloadTime: now
      });

      // Reload the page
      console.log(`Reloading page (attempt ${newAttempts}/${this.MAX_RELOAD_ATTEMPTS})...`);
      window.location.reload();
  }

  /**
   * Gets reload tracking data from sessionStorage
   */
  private getReloadData(): { attempts: number; lastReloadTime: number | null } {
    try {
      const data = sessionStorage.getItem(this.RELOAD_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        return {
          attempts: parsed.attempts || 0,
          lastReloadTime: parsed.lastReloadTime || null
        };
      }
    } catch (error) {
      console.warn('Error reading reload data:', error);
    }
    return { attempts: 0, lastReloadTime: null };
  }

  /**
   * Sets reload tracking data in sessionStorage
   */
  private setReloadData(data: { attempts: number; lastReloadTime: number }): void {
    try {
      sessionStorage.setItem(this.RELOAD_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Error setting reload data:', error);
    }
  }

  /**
   * Clears reload tracking data
   */
  private clearReloadData(): void {
    try {
      sessionStorage.removeItem(this.RELOAD_KEY);
    } catch (error) {
      console.warn('Error clearing reload data:', error);
    }
  }
}
